import {
    AfterViewInit,
    Component,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { EntityCollectionService, EntityServices } from '@ngrx/data';
import { select, Store } from '@ngrx/store';
import clone from 'just-clone';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { map, takeWhile } from 'rxjs/operators';
import { IChargeNumberManagement } from '../../contract-management/IChargeNumberManagement';
import * as fromCNStoreActions from '../../contract-management/store/charge-number-management.actions';
import * as fromCNStoreReducer from '../../contract-management/store/charge-number-management.reducer';
import * as fromDataServices from '../../data-services';
import { FeeLevel } from '../../data-services/fee-level/fee-level.model';
import * as fromLookups from '../../lookups';
import * as fromCommon from '../../shared';
import * as fromShared from '../../shared';
import { LoggingService } from '../../shared/services/logging/logging.service';
import * as fromUser from '../../user-info';
import { FeeLevelPresenterComponent } from '../presenter/fee-level-presenter.component';
@Component({
    selector: 'ef-fee-level-feature',
    templateUrl: './fee-level-feature.component.html',
    styleUrls: ['./fee-level-feature.component.scss'],
    standalone: false
})
export class FeeLevelFeatureComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    process$: Observable<fromShared.Process>;

    @ViewChild(FeeLevelPresenterComponent, { static: true })
    presenter: FeeLevelPresenterComponent;

    public feeLevels$: Observable<FeeLevel[]>;
    public feeOnGA$: Observable<fromDataServices.FeeOnGA.FeeOnGA[]>;
    public feeHistory$: Observable<fromDataServices.FeeHistory.FeeHistory[]>;
    public contracts$: Observable<fromDataServices.Contract.Contract[]>;
    public chargeNumbers$: Observable<IChargeNumberManagement[]>;
    public contractTypes$: Observable<fromLookups.IContractType[]>;
    public userInfo$: Observable<fromUser.IUserInfo>;

    selectedError$: Observable<fromShared.Errors.IShowErrors>;
    selectedErrorWithStatus$: Observable<fromCommon.IStatusMessage>;
    ShowError = new ReplaySubject<fromShared.Errors.IShowErrors>(1);

    active = true;
    contractDataService: EntityCollectionService<fromDataServices.Contract.Contract>;
    feeLevelDataService: EntityCollectionService<fromDataServices.FeeLevel.FeeLevel>;
    feeOnGADataService: EntityCollectionService<fromDataServices.FeeOnGA.FeeOnGA>;
    feeHistoryDataService: EntityCollectionService<fromDataServices.FeeHistory.FeeHistory>;

    constructor(
        public entityServices: EntityServices,
        private log: LoggingService,
        private store: Store
    ) {
        this.contractDataService =
            entityServices.getEntityCollectionService<fromDataServices.Contract.Contract>(
                fromDataServices.Contract.entityName
            );
        this.feeLevelDataService =
            entityServices.getEntityCollectionService<fromDataServices.FeeLevel.FeeLevel>(
                fromDataServices.FeeLevel.entityName
            );
        this.feeHistoryDataService =
            entityServices.getEntityCollectionService<fromDataServices.FeeHistory.FeeHistory>(
                fromDataServices.FeeHistory.entityName
            );
        this.feeOnGADataService =
            entityServices.getEntityCollectionService<fromDataServices.FeeOnGA.FeeOnGA>(
                fromDataServices.FeeOnGA.entityName
            );
    }

    ngOnInit(): void {
        this.store.dispatch(new fromLookups.LoadContractTypes());
        this.feeOnGADataService.getAll();
        this.contractDataService.getAll();
        this.feeLevelDataService.clearCache();
        //this.feeLevelDataService.getWithQuery({ ActiveOnly: 'true' });
        this.setupObservables();
        this.setupSubscriptions();
    }

    ngAfterViewInit(): void {}

    ngOnDestroy(): void {
        this.active = false;
        this.feeLevelDataService.clearCache();
        this.feeHistoryDataService.clearCache();
        // this.store.dispatch(fromActions.clearContractClasss());
    }

    setupObservables() {
        this.process$ = this.store.pipe(select(fromShared.selectCurrent));

        this.userInfo$ = this.store.pipe(select(fromUser.getUserInfo));

        this.feeLevels$ = this.feeLevelDataService.entities$.pipe(
            map(x => clone(x))
        );

        this.feeOnGA$ = this.feeOnGADataService.entities$.pipe(
            map(x => clone(x))
        );

        this.feeHistory$ = this.feeHistoryDataService.entities$.pipe(
            map(x => clone(x))
        );

        this.contractTypes$ = this.store.pipe(
            select(fromLookups.getContractTypes)
        );

        this.chargeNumbers$ = this.store.pipe(
            select(fromCNStoreReducer.selectAll)
            // map(chargenumbers => clone(chargenumbers))
        );

        this.contracts$ = this.contractDataService.entities$.pipe(
            //map(x => (x = x.filter(y => y.IsActive === true))),
            map(x => clone(x)),
            map(x =>
                x.sort((a, b) => a.ContractDesc.localeCompare(b.ContractDesc))
            )
        );
    }

    setupSubscriptions() {
        combineLatest([this.process$, this.ShowError])
            .pipe(takeWhile(() => this.active))
            .subscribe(([process, event]) => {
                this.store.dispatch(
                    new fromShared.Errors.SetShowErrors({
                        open: event.open,
                        MsgEntityID: event.MsgEntityID,
                        MsgProcessID: process.ProcessID
                    })
                );
            });
    }

    onLoadActiveInactiveFeeLevel($event) {
        //this.feeLevelDataService.clearCache();
        this.contractDataService.clearCache();
        this.contractDataService.getWithQuery({ActiveOnly: $event});
        //this.feeLevelDataService.getWithQuery({ ActiveOnly: $event });
    }

    onLoadFeeLevels($event) {
        this.log.verbose({
            type: 'FeeLevelFeatureComponent:onLoadFeeLevels'
        });
        this.feeLevelDataService.clearCache();
        if ($event.ContractList.length > 0) {
            this.feeLevelDataService.getWithQuery($event);
        }

    }

    onSaveFeeLevels($event) {
        this.log.verbose({
            type: 'FeeLevelFeatureComponent:onSaveFeeLevels'
        });
        this.feeLevelDataService.upsert($event);
    }

    onLoadContracts($event) {
        this.contractDataService.getAll();
    }

    onLoadChargeNumbers($event) {
        this.store.dispatch(
            fromCNStoreActions.getChargeNumbers({
                Contract: {
                    ContractListID: $event.toString(),
                    Activeonly: true
                }
            })
        );
    }

    onLoadFeeHistory($event) {
        this.log.verbose({
            type: 'FeeLevelFeatureComponent:onLoadFeeHistory'
        });
        this.feeHistoryDataService.clearCache();
        this.feeHistoryDataService.getWithQuery($event);
    }

    onSaveFeeHistory($event) {
        this.log.verbose({
            type: 'FeeLevelFeatureComponent:onSaveFeeHistory'
        });
        this.feeHistoryDataService.upsert($event);
    }

    onDeleteFeeLevel($event) {
        this.feeLevelDataService.delete($event);
    }

    // onShowError(event) {
    //     this.ShowError.next(event);
    // }
}
